import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDarkMode } from '../contexts/DarkModeContext';
import { AuthContext, useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useIsMobile } from '../hooks/useMediaQuery';
import { get } from '../utils/apiClient';
import { getNavigationItems } from '../config/navigationItems';
import { useProviderOnboardingStatus } from './onboarding/useProviderOnboardingStatus';
import { logger } from "../utils/logger";
import { prefetchRoute } from '../app/router/routePrefetch';

interface HamburgerMenuProps {
  /** Callback to open the ClaimOrgDialog (for "Name Your Clinic" menu item in demo mode). */
  onOpenClaimDialog?: () => void;
}

function HamburgerMenu({ onOpenClaimDialog }: HamburgerMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const menuButtonRef = useRef(null);
  const menuItemRefs = useRef([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useDarkMode();
  const isMobile = useIsMobile();
  const authContext = useContext(AuthContext);
  const { logout } = useAuth();
  const user = authContext?.user;
  const { hasPermission, loading } = usePermissions();

  // Fetch onboarding status for admins
  useEffect(() => {
    const fetchOnboardingStatus = async () => {
      if (!user || !hasPermission('settings.users')) return;
      try {
        const response = await get('/api/admin/onboarding/status');
        if (response.ok) {
          const data = await response.json();
          setOnboardingStatus(data);
        }
      } catch (error) {
        logger.error('Error fetching onboarding status:', error);
      }
    };
    fetchOnboardingStatus();
  }, [user, hasPermission]);

  // Provider onboarding (personality start) status — shared hook avoids duplicate API calls
  const { completed: providerOnboardingCompleted } = useProviderOnboardingStatus(user);

  // Sections that start collapsed to reduce cognitive load for new users.
  // Daily and Clinical stay expanded as they're the core workflow.
  const SECTIONS_DEFAULT_COLLAPSED = ['workspace', 'oversight', 'system', 'operations', 'my-account'];

  // Resolve the effective expanded state for a section, respecting defaults
  const isSectionExpanded = (sectionKey: string): boolean => {
    if (expandedSections[sectionKey] !== undefined) {
      return expandedSections[sectionKey];
    }
    return !SECTIONS_DEFAULT_COLLAPSED.includes(sectionKey);
  };

  // Toggle section expansion — resolves default state inside the updater
  // to avoid stale closures on rapid clicks before React re-renders
  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => {
      const currentlyExpanded = prev[sectionKey] !== undefined
        ? prev[sectionKey]
        : !SECTIONS_DEFAULT_COLLAPSED.includes(sectionKey);
      return { ...prev, [sectionKey]: !currentlyExpanded };
    });
  };

  const menuItems = getNavigationItems({ user, hasPermission, loading, onboardingStatus, providerOnboardingCompleted, onOpenClaimDialog });

  const handleLogout = async () => {
    await logout('/login');
  };

    const handleMenuItemClick = (item) => {
      if (item.action) {
        item.action();
      } else if (item.route === 'logout') {
        handleLogout();
      } else {
        navigate(item.route);
      }
      setIsOpen(false);
    };

    // Get only navigable items that are actually visible (excludes section
    // headers, dividers, subsections, and items hidden by collapsed sections)
    const navigableItems = menuItems.filter(item => {
      if (item.type === 'section' || item.type === 'divider' || item.type === 'subsection') return false;
      if (item.parentSection && !isSectionExpanded(item.parentSection)) return false;
      return true;
    });

    // Focus first menu item when menu opens
    useEffect(() => {
      if (isOpen && menuItemRefs.current[0]) {
        setFocusedIndex(0);
        menuItemRefs.current[0]?.focus();
      }
    }, [isOpen]);

    // Handle keyboard navigation in the menu
    const handleMenuKeyDown = useCallback((event) => {
      if (!isOpen) return;

      const itemCount = navigableItems.length;
    
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          setIsOpen(false);
          menuButtonRef.current?.focus();
          break;
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex(prev => {
            const next = prev < itemCount - 1 ? prev + 1 : 0;
            menuItemRefs.current[next]?.focus();
            return next;
          });
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex(prev => {
            const next = prev > 0 ? prev - 1 : itemCount - 1;
            menuItemRefs.current[next]?.focus();
            return next;
          });
          break;
        case 'Home':
          event.preventDefault();
          setFocusedIndex(0);
          menuItemRefs.current[0]?.focus();
          break;
        case 'End':
          event.preventDefault();
          setFocusedIndex(itemCount - 1);
          menuItemRefs.current[itemCount - 1]?.focus();
          break;
        case 'Tab':
          // Allow Tab to close menu and move focus naturally
          setIsOpen(false);
          break;
        default:
          break;
      }
    }, [isOpen, navigableItems.length]);

    // Set ref for a navigable item
    const setMenuItemRef = useCallback((index) => (el) => {
      menuItemRefs.current[index] = el;
    }, []);

  return (
    <div className="relative">
      {/* Hamburger icon */}
      <button
        ref={menuButtonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`p-3 md:p-2 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'} focus:outline-none focus:ring-2 focus:ring-blue-400 transition`}
        aria-label="Open navigation menu"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Overlay menu */}
      {isOpen && (
        <div className="fixed inset-0 overlay-root z-50">
          {/* Dark backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 cursor-pointer"
            style={{
              top: isMobile ? '3.5rem' : '3.5rem',
            }}
            data-interactive="true"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
            role="button"
            tabIndex={-1}
            aria-label="Close menu"
          />

          {/* Slide-out panel - Full screen on mobile, sidebar on desktop */}
          <div 
            className={`fixed top-[3.5rem] left-0 h-[calc(100vh-3.5rem)] flex flex-col ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} shadow-2xl transition-transform`}
            style={{
              width: isMobile ? '100%' : '264px',
              maxWidth: '100vw',
            }}
            data-interactive="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-shrink-0 flex justify-between items-center p-4 border-b border-gray-300 dark:border-gray-700">
              <h2 id="menu-heading" className="text-lg font-semibold">Menu</h2>
              <button 
                onClick={() => setIsOpen(false)} 
                className="p-2 md:p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 min-w-[44px] min-h-[44px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-400"
                aria-label="Close menu"
              >
                <svg className="w-5 h-5" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <nav 
              className="flex-1 mt-2 overflow-y-auto" 
              style={{ 
                paddingBottom: isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 5rem)' : '1rem',
              }}
              role="menu"
              aria-labelledby="menu-heading"
              onKeyDown={handleMenuKeyDown}
            >
              <ul role="none">
                {(() => {
                  let navIndex = 0;
                  
                  return menuItems.map((item, index) => {
                    // Divider
                    if (item.type === 'divider') {
                      return (
                        <li key={`divider-${index}`} className="my-2" role="none">
                          <hr className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} />
                        </li>
                      );
                    }
                    
                    // Section header
                    if (item.type === 'section') {
                      return (
                        <li key={`section-${index}`} className="px-4 pt-4 pb-2" role="none">
                          <button
                            onClick={() => item.collapsible && toggleSection(item.key)}
                            className={`flex items-center justify-between w-full text-left ${item.collapsible ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                            type="button"
                          >
                            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-2">
                              {item.icon && <span>{item.icon}</span>}
                              {item.label}
                            </h3>
                            {item.collapsible && (
                              <svg 
                                className={`w-4 h-4 text-gray-400 transition-transform ${isSectionExpanded(item.key) ? 'rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            )}
                          </button>
                        </li>
                      );
                    }
                    
                    // Subsection header
                    if (item.type === 'subsection') {
                      if (item.parentSection && !isSectionExpanded(item.parentSection)) return null;
                      return (
                        <li key={`subsection-${index}`} className="px-4 pt-3 pb-1" role="none">
                          <span className="text-xs font-medium text-gray-400 dark:text-gray-500">
                            {item.label}
                          </span>
                        </li>
                      );
                    }

                    // Skip items in collapsed sections
                    if (item.parentSection && !isSectionExpanded(item.parentSection)) {
                      return null;
                    }

                    const currentNavIndex = navIndex;
                    navIndex++;

                    const isActive = item.route && (
                      location.pathname === item.route ||
                      (item.route !== 'logout' && item.route !== '/ai-assistant' && location.pathname.startsWith(item.route))
                    );

                    return (
                      <li key={item.route || item.label || `item-${index}`} role="none">
                        <button
                          ref={setMenuItemRef(currentNavIndex)}
                          onClick={() => handleMenuItemClick(item)}
                          onMouseEnter={() => item.route && item.route !== 'logout' && prefetchRoute(item.route)}
                          role="menuitem"
                          aria-label={item.description ? `${item.label} — ${item.description}` : item.label}
                          tabIndex={currentNavIndex === focusedIndex ? 0 : -1}
                          className={`w-full flex items-start justify-start text-left px-4 py-3 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${
                            item.highlight
                              ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500'
                              : isActive
                                ? isDarkMode
                                  ? 'bg-gray-700 text-apple-blue'
                                  : 'bg-blue-50 text-apple-blue'
                                : isDarkMode
                                  ? 'hover:bg-gray-700'
                                  : 'hover:bg-gray-100'
                          } transition`}
                        >
                          <div>
                            <span className={item.highlight ? 'font-medium text-blue-600 dark:text-blue-400' : ''}>
                              {item.label}
                            </span>
                            {item.description && (
                              <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5" aria-hidden="true">{item.description}</span>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  });
                })()}
              </ul>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}

export default HamburgerMenu;
